// Cartão suave exibido sobre a câmera quando um rosto é identificado: foto de referência,
// nome, categoria e o movimento registrado (entrada, saída, repetida ou saída em confirmação).
import { useEffect, useState } from "react";
import { Ban, CheckCircle2, LogIn, LogOut, RotateCcw, UserRound } from "lucide-react";
import type { FacialObservationResponse } from "@centro-cirurgico/contracts";
import { formatDuration, formatTime, initials } from "@centro-cirurgico/ui";

import { apiUrl } from "../utils/api";

export type IdentificationTone = "entry" | "exit" | "pending" | "repeat" | "blocked" | "cancelled";

export interface IdentificationCardProps {
  response: FacialObservationResponse;
  /** Progresso (0..1) da permanência exigida para a saída. */
  holdProgress: number | null;
  /** A confirmação da saída foi interrompida. */
  cancelled?: boolean;
  /** Mensagem extra (ex.: saída recusada pelo servidor). */
  note?: string | null;
}

function minutesSince(fromIso: string | null, toIso: string | null): number | null {
  if (!fromIso) return null;
  const from = Date.parse(fromIso);
  const to = toIso ? Date.parse(toIso) : Date.now();
  if (!Number.isFinite(from) || !Number.isFinite(to)) return null;
  return Math.max(0, (to - from) / 60_000);
}

export function identificationTone(response: FacialObservationResponse, cancelled = false): IdentificationTone {
  if (response.status === "blocked") return "blocked";
  if (cancelled) return "cancelled";
  switch (response.action) {
    case "exit":
      return "exit";
    case "exit_pending":
      return "pending";
    case "repeat_entry":
    case "repeat_exit":
      return "repeat";
    default:
      return "entry";
  }
}

function headline(response: FacialObservationResponse, tone: IdentificationTone): { title: string; detail: string } {
  const time = formatTime(response.capturedAt);
  switch (tone) {
    case "blocked":
      return { title: "Acesso não autorizado", detail: "Procure a coordenação do centro cirúrgico." };
    case "cancelled":
      return {
        title: "Saída não registrada",
        detail: "Você saiu da frente do totem antes do tempo. Para sair, fique parado até o círculo completar."
      };
    case "pending": {
      const inside = formatDuration(minutesSince(response.enteredAt, null));
      return {
        title: "Registrando sua saída",
        detail: `Fique parado em frente ao totem. Entrada às ${formatTime(response.enteredAt)} (${inside}).`
      };
    }
    case "exit": {
      const inside = formatDuration(minutesSince(response.enteredAt, response.capturedAt));
      return {
        title: `às ${time}`,
        detail: `Entrada às ${formatTime(response.enteredAt)} · ${inside} no centro cirúrgico.`
      };
    }
    case "repeat":
      return response.action === "repeat_exit"
        ? { title: `Saída já registrada às ${time}`, detail: "Nenhum registro novo foi feito." }
        : {
            title: `Entrada já registrada às ${time}`,
            detail: "Para registrar a saída, valide o rosto de novo daqui a alguns minutos."
          };
    default:
      return {
        title: `às ${time}`,
        detail: "Na saída, valide o rosto de novo e fique parado por alguns segundos."
      };
  }
}

function ToneIcon({ tone }: { tone: IdentificationTone }) {
  const props = { "aria-hidden": true as const, size: 26 };
  if (tone === "blocked") return <Ban {...props} />;
  if (tone === "exit" || tone === "pending") return <LogOut {...props} />;
  if (tone === "repeat" || tone === "cancelled") return <RotateCcw {...props} />;
  return <LogIn {...props} />;
}

const toneLabel: Record<IdentificationTone, string> = {
  entry: "Entrada",
  exit: "Saída",
  pending: "Saída",
  repeat: "Já registrado",
  blocked: "Bloqueado",
  cancelled: "Cancelado"
};

function ReferencePhoto({ url, name }: { url: string | null; name: string }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [url]);
  if (!url || failed) {
    return (
      <span className="id-photo id-photo-fallback" aria-hidden="true">
        {name ? initials(name) : <UserRound size={40} />}
      </span>
    );
  }
  return (
    <img
      className="id-photo"
      src={apiUrl(url)}
      alt={`Foto de referência de ${name}`}
      onError={() => setFailed(true)}
      draggable={false}
    />
  );
}

/** Anel grande de contagem ao redor da foto durante a confirmação da saída. */
function HoldRing({ progress }: { progress: number }) {
  const radius = 60;
  const circumference = 2 * Math.PI * radius;
  return (
    <svg className="id-hold-ring" viewBox="0 0 136 136" aria-hidden="true">
      <circle cx="68" cy="68" r={radius} className="id-hold-track" />
      <circle
        cx="68"
        cy="68"
        r={radius}
        className="id-hold-value"
        strokeDasharray={circumference}
        strokeDashoffset={circumference * (1 - progress)}
      />
    </svg>
  );
}

export function IdentificationCard({ response, holdProgress, cancelled = false, note }: IdentificationCardProps) {
  const baseTone = identificationTone(response, cancelled);
  // Saída pendente sem contagem em andamento = confirmação interrompida ou recusada.
  const tone: IdentificationTone = baseTone === "pending" && holdProgress === null ? "cancelled" : baseTone;
  const name = response.professionalName ?? "Profissional";
  const { title, detail } = headline(response, tone);

  // Saída em confirmação: tela de "aguarde" (fundo claro, anel grande e contagem), bem
  // diferente do resultado registrado (faixa sólida com ícone de concluído).
  if (tone === "pending" && holdProgress !== null) {
    const holdMs = response.holdMs ?? 3000;
    const remaining = Math.max(1, Math.ceil((holdMs * (1 - holdProgress)) / 1000));
    return (
      <div className="id-card id-card-hold" role="status" aria-live="assertive" aria-atomic="true">
        <span className="id-hold-eyebrow">
          <LogOut aria-hidden="true" size={16} /> Confirmando saída
        </span>
        <div className="id-hold-stage">
          <HoldRing progress={holdProgress} />
          <ReferencePhoto url={response.referencePhotoUrl} name={name} />
          <span className="id-hold-count" aria-hidden="true">
            {remaining}
          </span>
        </div>
        <strong className="id-hold-title">Fique parado</strong>
        <span className="id-hold-detail">
          {name} · saída em {remaining} s
        </span>
        <span className="sr-only">{detail}</span>
      </div>
    );
  }

  const done = tone === "entry" || tone === "exit";
  return (
    <div className={`id-card id-card-result tone-${tone}`} role="status" aria-live="assertive" aria-atomic="true">
      <div className="id-band">
        {done ? <CheckCircle2 aria-hidden="true" size={30} /> : <ToneIcon tone={tone} />}
        <span className="id-band-copy">
          <span className="id-band-label">{done ? `${toneLabel[tone]} registrada` : toneLabel[tone]}</span>
          <span className="id-band-title">{title}</span>
        </span>
      </div>
      <div className="id-body">
        <ReferencePhoto url={response.referencePhotoUrl} name={name} />
        <div className="id-copy">
          <strong className="id-name">{name}</strong>
          {response.roleLabel && <span className="id-role">{response.roleLabel}</span>}
          <span className="id-detail">{note ?? detail}</span>
        </div>
      </div>
    </div>
  );
}
